import React, { useEffect, useState, useRef } from "react";
import "./OrderSummary.css";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatRupees } from "../utils/money";
import {
  getCafeMenuCart,
  totalsOf,
  initiateCafeMenuPayment,
  getCafeMenuOrderStatus,
  CafeMenuApiError,
} from "../cafemenuApi";
import { openRazorpayCheckout } from "../utils/razorpayCheckout";
import { listenToCafeMenuOrderStatus } from "../firebaseOrderStatus";
import {
  trackCartOrderDone,
  trackCartSummaryEditClicked,
  trackCartSummaryViewed,
  trackCartConversionCompleted,
} from "../utils/analytics";

const formatTime = () => {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  const day = now.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${day} ${months[now.getMonth()]}, ${h12}:${m} ${ampm}`;
};

const NEW_TAG_ID = "6932636dec4624b7afca0266";

// Dietary indicators (veg / non-veg / egg / spicy) for a cart item.
const renderDietaryTags = (item) => {
  const tags = Array.isArray(item.tags) ? item.tags : [];
  if (tags.length === 0) return null;
  const hasEgg = tags.some((t) => t?.name?.toLowerCase().includes("egg"));
  const hasNonVeg = tags.some((t) => {
    const n = t?.name?.toLowerCase() || "";
    return n.includes("non") && n.includes("veg");
  });
  const icons = [];
  if (hasEgg) {
    icons.push(<img key="egg" src="/EggTag.png" alt="Egg" className="order-summary-diet-egg" />);
  }
  tags.forEach((t, i) => {
    const name = t?.name?.toLowerCase() || "";
    if (t?.id === NEW_TAG_ID || name.includes("new")) return;
    if (hasEgg && name.includes("egg")) return;
    if (hasEgg && hasNonVeg && name.includes("non")) return;
    if (t?.tagIcon?.fullPath) {
      icons.push(
        <img key={t.id || i} src={t.tagIcon.fullPath} alt={t.name || ""} className="order-summary-diet-icon" />
      );
    }
  });
  if (icons.length === 0) return null;
  return <span className="order-summary-diet-tags">{icons}</span>;
};

function ItemThumb({ src, quantity }) {
  const [err, setErr] = useState(false);
  return (
    <div className="order-summary-thumb">
      {src && !err ? (
        <img src={src} alt="" className="order-summary-thumb-img" onError={() => setErr(true)} />
      ) : (
        <div className="order-summary-thumb-ph" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M4 8h13a3 3 0 0 1 0 6h-1M4 8v6a5 5 0 0 0 5 5h3a5 5 0 0 0 5-5V8H4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
            <path d="M8 2.5c-.5.6-.5 1.4 0 2M11.5 2.5c-.5.6-.5 1.4 0 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </div>
      )}
      <span className="order-summary-thumb-qty">{quantity}</span>
    </div>
  );
}

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // ~30s
// The poll above gives up after ~30s, so the "what's happening" notice has to
// appear inside that window (the mobile app waits 2 min because it has no
// such cut-off) — otherwise it could never be seen.
const HIGH_TRAFFIC_TIMEOUT_MS = 15 * 1000;
// If the order still isn't placed when the fast poll gives up, the customer
// is already shown "payment confirmed, still placing" — but we keep watching
// (live push + a slow poll) so that screen upgrades by itself the moment the
// kitchen order lands, instead of staying stale forever.
const SLOW_POLL_INTERVAL_MS = 10000;
const SLOW_POLL_MAX_ATTEMPTS = 90; // ~15 min

export default function OrderSummary({ cafeId, cafeName, onEditOrder, onOrderDone }) {
  const {
    items,
    itemCount,
    subtotal,
    taxTotal,
    grandTotal,
    serverTotals,
    setServerTotals,
    phoneNumber: cartPhoneNumber,
    tableNumber,
    orderType,
    appliedDiscount,
    appliedReward,
    cafeMenuCartId,
    clearCart,
  } = useCart();
  const { isAuthenticated, token, phoneNumber: authPhoneNumber, holdSession } = useAuth();
  // The cart's local phone field stays empty when OTP verification was
  // skipped via an existing session — fall back to the verified auth phone.
  const phoneNumber = cartPhoneNumber || (authPhoneNumber || "").replace(/\D/g, "").slice(-10);

  // Server-confirmed amounts (set when the reward/discount was applied on
  // the rewards step) — not re-estimated here.
  const discountAmount = appliedDiscount?.amount || 0;
  const rewardAmount = appliedReward?.amount || 0;
  // What the backend actually charges: item prices are final (the Rista sale
  // is sent price-inclusive, taxes are inside them), minus any reward/offer.
  // Never add the tax estimate on top — that made a fully covered bill ask
  // for a few rupees of "tax" while the server total was 0.
  // The server's figure is what gets charged (reward/offer off the item prices
  // first, then GST on what is left — the app's bill). Fetched fresh here so a
  // reload or a stale screen can never show a different number than Razorpay.
  const hasServerTotals = Boolean(serverTotals && serverTotals.cartId === cafeMenuCartId);
  const taxAmount = hasServerTotals ? serverTotals.taxAmount : 0;
  const finalTotal = hasServerTotals ? Math.round(serverTotals.totalPrice * 100) / 100 : 0;

  const [confirmingClear, setConfirmingClear] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payStage, setPayStage] = useState(""); // "" | "saving" | "razorpay" | "confirming"
  const [payError, setPayError] = useState("");
  const [orderResult, setOrderResult] = useState(null); // { invoiceNumber, ristaPushStatus }
  const [showHighTrafficNotice, setShowHighTrafficNotice] = useState(false);
  const [paymentFailedReason, setPaymentFailedReason] = useState("");
  const pollTimerRef = useRef(null);
  const highTrafficTimerRef = useRef(null);
  const firebaseUnsubscribeRef = useRef(null);
  const settledRef = useRef(false);
  const upgradeRef = useRef(false);
  const releaseSessionRef = useRef(null);
  const payInFlightRef = useRef(false);

  // Refresh the server's totals when this screen opens (and if the cart id
  // changes), so what is shown is always what will be charged.
  useEffect(() => {
    if (!token || !cafeMenuCartId) return undefined;
    let cancelled = false;
    getCafeMenuCart(token, cafeMenuCartId)
      .then((cart) => { if (!cancelled) setServerTotals(totalsOf(cart)); })
      .catch(() => { /* keep whatever the cart step already stored */ });
    return () => { cancelled = true; };
  }, [token, cafeMenuCartId, setServerTotals]);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (highTrafficTimerRef.current) clearTimeout(highTrafficTimerRef.current);
      if (firebaseUnsubscribeRef.current) firebaseUnsubscribeRef.current();
      if (releaseSessionRef.current) releaseSessionRef.current();
    };
  }, []);

  // The login can expire (visit-scoped) while the customer sits on the
  // summary. Send them back to re-verify instead of leaving a Pay button
  // that can only fail — but never while a payment is in flight.
  useEffect(() => {
    if (!isAuthenticated && !paying && !orderResult && !paymentFailedReason) {
      onEditOrder();
    }
  }, [isAuthenticated, paying, orderResult, paymentFailedReason, onEditOrder]);

  useEffect(() => {
    if (itemCount === 0 && !orderResult) {
      onOrderDone();
    }
  }, [itemCount, orderResult, onOrderDone]);

  useEffect(() => {
    trackCartSummaryViewed({
      cafeId,
      cafeName,
      items,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    });
  }, [cafeId, cafeName, items, itemCount, subtotal, taxTotal, grandTotal, tableNumber, phoneNumber]);

  // The order could not be placed and the payment was returned: that's final.
  const showRefunded = () => {
    settledRef.current = true;
    stopWatching();
    setShowHighTrafficNotice(false);
    setPayStage("");
    setPaying(false);
    setPaymentFailedReason("We couldn't place your order, so your payment has been refunded. It reaches your account in 5–7 working days.");
  };

  const pollOrderStatus = (cartId, attempt = 0) => {
    getCafeMenuOrderStatus(token, cartId)
      .then((data) => {
        if (data.ristaPushStatus === "REFUNDED") { showRefunded(); return; }
        const settled = data.ristaPushStatus === "SUCCESS" || data.ristaPushStatus === "FAILED";
        if (settled || attempt >= POLL_MAX_ATTEMPTS) {
          finishOrder(data);
          return;
        }
        pollTimerRef.current = setTimeout(() => pollOrderStatus(cartId, attempt + 1), POLL_INTERVAL_MS);
      })
      .catch(() => {
        // Payment already succeeded at this point — a status-check hiccup
        // should never look like a failed order. Fall back to "processing".
        finishOrder({ ristaPushStatus: "PENDING" });
      });
  };

  // Final success: stop every watcher and let go of the session hold.
  const stopWatching = () => {
    upgradeRef.current = false;
    if (releaseSessionRef.current) {
      releaseSessionRef.current();
      releaseSessionRef.current = null;
    }
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (highTrafficTimerRef.current) clearTimeout(highTrafficTimerRef.current);
    if (firebaseUnsubscribeRef.current) {
      firebaseUnsubscribeRef.current();
      firebaseUnsubscribeRef.current = null;
    }
  };

  const upgradeToPlaced = (invoiceNumber) => {
    stopWatching();
    setOrderResult({ invoiceNumber: invoiceNumber || null, ristaPushStatus: "SUCCESS" });
  };

  const slowPollOrderStatus = (cartId, attempt = 0) => {
    if (attempt >= SLOW_POLL_MAX_ATTEMPTS) return;
    pollTimerRef.current = setTimeout(() => {
      getCafeMenuOrderStatus(token, cartId)
        .then((data) => {
          if (!upgradeRef.current) return;
          if (data.ristaPushStatus === "REFUNDED") showRefunded();
          else if (data.ristaPushStatus === "SUCCESS") upgradeToPlaced(data.ristaInvoiceNumber);
          else slowPollOrderStatus(cartId, attempt + 1);
        })
        .catch(() => {
          if (upgradeRef.current) slowPollOrderStatus(cartId, attempt + 1);
        });
    }, SLOW_POLL_INTERVAL_MS);
  };

  const finishOrder = (data) => {
    if (settledRef.current) return;
    settledRef.current = true;
    const placed = data.ristaPushStatus === "SUCCESS";
    if (placed) {
      stopWatching();
    } else {
      // Keep the live listener + session hold; just move to slow polling.
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (highTrafficTimerRef.current) clearTimeout(highTrafficTimerRef.current);
      upgradeRef.current = true;
      slowPollOrderStatus(cafeMenuCartId);
    }
    setShowHighTrafficNotice(false);
    setPayStage("");
    setPaying(false);
    setOrderResult({
      invoiceNumber: data.ristaInvoiceNumber || null,
      ristaPushStatus: data.ristaPushStatus || "PENDING",
    });
    const commonParams = {
      cafeId,
      cafeName,
      items,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    };
    trackCartOrderDone(commonParams);
    trackCartConversionCompleted(commonParams);
  };

  const handlePay = async () => {
    if (payInFlightRef.current || paying) return;
    if (!isAuthenticated || !token) {
      setPayError("Please verify your mobile number before paying.");
      return;
    }
    if (!cafeMenuCartId) {
      setPayError("Your cart is still saving — please go back a step and try again.");
      return;
    }
    setPayError("");
    setPaymentFailedReason("");
    setShowHighTrafficNotice(false);
    settledRef.current = false;
    if (releaseSessionRef.current) releaseSessionRef.current();
    releaseSessionRef.current = holdSession();
    setPaying(true);
    setPayStage("razorpay");
    payInFlightRef.current = true;
    try {
      // The cart (including any applied reward/discount) was already saved
      // and validated server-side on the rewards step — nothing to redo here.
      const payment = await initiateCafeMenuPayment(token, cafeMenuCartId);

      // Shared by the paid (after Razorpay succeeds) and free (nothing to pay)
      // paths: watch the order until the backend reports it placed.
      const startConfirming = () => {
      setPayStage("confirming");
      pollOrderStatus(cafeMenuCartId);

      // Live push, same RTDB path the main app already listens to —
      // short-circuits the poll the moment the backend knows more, and
      // is the only way "payment succeeded but Rista placement is still
      // pending" / "payment actually failed after webhook" are
      // distinguished before the poll's own ~30s window elapses.
      firebaseUnsubscribeRef.current = listenToCafeMenuOrderStatus(cafeMenuCartId, (data) => {
        if (settledRef.current && !upgradeRef.current) return;
        if (data.status === "placed" || data.status === "completed") {
          if (upgradeRef.current) upgradeToPlaced(data.ristaInvoiceNumber);
          else finishOrder({ ristaPushStatus: "SUCCESS", ristaInvoiceNumber: data.ristaInvoiceNumber });
        } else if (data.status === "failed") {
          // A genuine payment failure reported after the Razorpay sheet
          // already told us "success" — rare (e.g. a captured payment
          // reversed moments later), but must never be silently treated
          // as "still placing the order".
          settledRef.current = true;
          stopWatching();
          setPaymentFailedReason(data.reason || "Your payment could not be completed.");
        } else if (data.status === "manual_reconciliation" && !upgradeRef.current) {
          setShowHighTrafficNotice(true);
        }
      });

      highTrafficTimerRef.current = setTimeout(() => {
        if (!settledRef.current) setShowHighTrafficNotice(true);
      }, HIGH_TRAFFIC_TIMEOUT_MS);
      };

      // Points / a reward / an offer covered the whole bill: the backend has
      // already placed the order, so there is nothing to pay — no Razorpay.
      if (payment.isFree) {
        startConfirming();
        return;
      }

      await openRazorpayCheckout({
        keyId: payment.keyId,
        amount: payment.amount,
        currency: payment.currency,
        orderId: payment.razorpayOrderId,
        name: cafeName || "Blue Tokai Coffee Roasters",
        description: orderType === "takeaway" ? "Takeaway" : `Table #${tableNumber}`,
        contact: phoneNumber,
        onSuccess: startConfirming,
      });
    } catch (e) {
      if (releaseSessionRef.current) {
        releaseSessionRef.current();
        releaseSessionRef.current = null;
      }
      const message = e?.message || "";
      if (message.includes("already been checked out")) {
        // This cart was paid in an earlier attempt (tab closed, UPI app
        // switch, retry after a slow confirmation) — show that order's real
        // status instead of an error the customer can't act on.
        setPaying(true);
        setPayStage("confirming");
        settledRef.current = false;
        releaseSessionRef.current = holdSession();
        pollOrderStatus(cafeMenuCartId);
        return;
      }
      if (message === "dismissed") {
        setPayError("Payment cancelled. If money was deducted (e.g. UPI still pending), your order confirms automatically — otherwise you can try again.");
      } else {
        setPayError(e instanceof CafeMenuApiError ? e.message : message || "Something went wrong. Please try again.");
      }
      setPaying(false);
      setPayStage("");
    } finally {
      payInFlightRef.current = false;
    }
  };

  if (paymentFailedReason) {
    return (
      <div className="order-summary order-summary-done">
        <div className="order-done-content">
          <h2 className="order-done-title">Payment Issue</h2>
          <p className="order-done-subtitle">
            {paymentFailedReason}
            {/refund|returned/i.test(paymentFailedReason) ? "" : " Please contact support before trying again — if you were charged, it will be refunded."}
          </p>
          <button
            className="cart-step-btn-primary order-done-cta"
            onClick={() => {
              setPaymentFailedReason("");
              setPayError("");
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (orderResult) {
    const success = orderResult.ristaPushStatus === "SUCCESS";
    const savedAmount = discountAmount + rewardAmount;
    const isFree = hasServerTotals && finalTotal === 0;
    const rows = [
      cafeName ? { label: "Café", value: cafeName } : null,
      orderType === "takeaway"
        ? { label: "Order", value: "Takeaway" }
        : tableNumber ? { label: "Table", value: `#${tableNumber}` } : null,
      orderResult.invoiceNumber ? { label: "Order no.", value: `#${orderResult.invoiceNumber}` } : null,
      { label: "Items", value: `${itemCount} item${itemCount === 1 ? "" : "s"}` },
    ].filter(Boolean);
    return (
      <div className="order-summary order-summary-done">
        <div className="order-done-content order-done-content-rich">
          <div className="order-done-check order-done-check-glow">
            <svg viewBox="0 0 52 52" className="order-done-svg">
              <circle className="order-done-circle" cx="26" cy="26" r="24" />
              <path className="order-done-tick" d="M14 27l7 7 16-16" />
            </svg>
          </div>
          <h2 className="order-done-title">{success ? (isFree ? "Order confirmed!" : "Order placed!") : "Payment confirmed"}</h2>
          <p className="order-done-subtitle">
            {success
              ? `Your order has been successfully placed${cafeName ? ` at ${cafeName}` : ""}.${orderType === "takeaway" ? " We'll pack it for takeaway." : tableNumber ? ` We'll bring it to Table #${tableNumber}.` : ""}`
              : "We're still placing your order. Please show this screen to the barista — it updates by itself once it's placed."}
          </p>

          <div className="order-done-card">
            <div className="order-done-amount">
              <span className="order-done-amount-label">Amount paid</span>
              <strong className="order-done-amount-value">{isFree ? "₹0" : `₹${formatRupees(finalTotal)}`}</strong>
              {isFree && <span className="order-done-amount-note">Fully covered by your Circle reward</span>}
            </div>
            {savedAmount > 0 && (
              <div className="order-done-saved">🎉 You saved ₹{formatRupees(savedAmount)}</div>
            )}
            <dl className="order-done-rows">
              {rows.map((row) => (
                <div key={row.label} className="order-done-row">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>


          <button
            className="cart-step-btn-primary order-done-cta"
            onClick={() => {
              clearCart();
              setOrderResult(null);
              onOrderDone();
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-summary">
      <div className="order-summary-hero">
        <img
          src="/BTCArtSmall.png"
          alt=""
          className="order-summary-art"
          aria-hidden="true"
        />
        <div className="order-summary-hero-content">
          <div className="order-summary-hero-top">
            <div className="order-summary-logo-wrap">
              <img src="/BTC_Blue_Paradise.png" alt="Blue Tokai" className="order-summary-logo" />
            </div>
            {orderType === "takeaway" ? (
              <div className="order-summary-table-badge">
                <span className="order-summary-table-label">Order</span>
                <span className="order-summary-table-num">Takeaway</span>
              </div>
            ) : tableNumber && (
              <div className="order-summary-table-badge">
                <span className="order-summary-table-label">Table</span>
                <span className="order-summary-table-num">#{tableNumber}</span>
              </div>
            )}
          </div>
          <h1 className="order-summary-heading">Order Summary</h1>
          <div className="order-summary-meta-line">
            {cafeName && <span>{cafeName}</span>}
            <span className="order-summary-meta-dot">·</span>
            <span>{formatTime()}</span>
            <span className="order-summary-meta-dot">·</span>
            <span>{itemCount} item{itemCount === 1 ? "" : "s"}</span>
          </div>
          {phoneNumber && (
            <div className="order-summary-hero-phone">
              <span className="order-summary-hero-phone-label">Mobile</span>
              <span className="order-summary-hero-phone-value">+91 {phoneNumber}</span>
            </div>
          )}
        </div>
      </div>

      <div className="order-summary-barista-pill-wrap">
        <div className="order-summary-barista-pill">
          <span className="order-summary-barista-dot" />
          <span className="order-summary-barista-text">{hasServerTotals && finalTotal === 0 ? "Place your order to confirm it" : "Pay now to place your order"}</span>
        </div>
      </div>

      <div className="order-summary-body">
        <div className="order-summary-body-inner">
          <div className="order-summary-items">
            {items.map((item) => (
              <div className="order-summary-row" key={item.id}>
                <ItemThumb src={item.imageUrl} quantity={item.quantity} />
                <div className="order-summary-item-info">
                  <p className="order-summary-item-name">
                    {renderDietaryTags(item)}
                    <span className="order-summary-item-name-text">{item.name}</span>
                  </p>
                  {(item.variantTitle || item.selectedOptions?.length > 0) && (
                    <p className="order-summary-item-options">
                      {[
                        item.variantTitle,
                        ...(item.selectedOptions || []).map((o) =>
                          Number(o?.price) > 0 ? `${o.title} (+₹${Math.round(o.price)})` : o.title
                        ),
                      ].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <span className="order-summary-item-price">₹{Math.round(item.unitPrice * item.quantity)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="order-summary-footer">
        <div className="order-summary-totals">
          <div className="order-summary-totals-row">
            <span>Items Total</span>
            <span>₹{formatRupees(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="order-summary-totals-row order-summary-totals-saving">
              <span>{appliedDiscount?.name || "Offer"} applied</span>
              <span>−₹{formatRupees(discountAmount)}</span>
            </div>
          )}
          {rewardAmount > 0 && (
            <div className="order-summary-totals-row order-summary-totals-saving">
              <span>{appliedReward?.name || "Reward"} applied</span>
              <span>−₹{formatRupees(rewardAmount)}</span>
            </div>
          )}
          <div className="order-summary-totals-row">
            <span>GST</span>
            <span>{hasServerTotals ? `+₹${formatRupees(taxAmount)}` : "…"}</span>
          </div>
          <div className="order-summary-totals-grand">
            <span>Total Payable</span>
            <strong>{hasServerTotals ? `₹${formatRupees(finalTotal)}` : "…"}</strong>
          </div>
        </div>

        {payError && <p className="cart-step-error order-summary-pay-error">{payError}</p>}

        {showHighTrafficNotice && payStage === "confirming" && (
          <div className="order-summary-traffic-notice">
            <p className="order-summary-traffic-notice-title">What's happening?</p>
            <p className="order-summary-traffic-notice-text">
              We're facing high traffic on our backend. Your payment is initiated; we'll notify you as soon as it is verified. Please keep this screen open.
            </p>
          </div>
        )}

        {!confirmingClear ? (
          <div className="order-summary-pay-section">
            <button
              className="cart-step-btn-primary order-summary-pay-btn"
              onClick={handlePay}
              disabled={paying || !hasServerTotals}
            >
              {payStage === "razorpay" && "Opening payment…"}
              {payStage === "confirming" && "Confirming your order…"}
              {!payStage && (!hasServerTotals ? "Loading…" : finalTotal === 0 ? "Place order" : `Pay ₹${formatRupees(finalTotal)}`)}
            </button>
            <div className="order-summary-actions">
              <button
                className="order-summary-done-btn"
                onClick={() => {
                  trackCartSummaryEditClicked({
                    cafeId,
                    cafeName,
                    itemCount,
                    lineCount: items.length,
                    subtotal,
                    taxTotal,
                    grandTotal,
                    hasTableNumber: Boolean(tableNumber),
                    hasPhoneNumber: Boolean(phoneNumber),
                  });
                  onEditOrder();
                }}
                disabled={paying}
              >
                ← Edit Order
              </button>
              <button className="order-summary-edit-btn" onClick={() => setConfirmingClear(true)} disabled={paying}>
                Clear Cart
              </button>
            </div>
          </div>
        ) : (
          <div className="order-summary-confirm">
            <p className="order-summary-confirm-text">Clear cart and start fresh?</p>
            <div className="order-summary-confirm-actions">
              <button
                className="order-summary-confirm-yes"
                onClick={() => {
                  clearCart();
                  onOrderDone();
                }}
              >
                Yes, Clear
              </button>
              <button className="order-summary-confirm-no" onClick={() => setConfirmingClear(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
